import { Suspense, lazy, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import AuthGuard from './components/AuthGuard';
import DashboardLayout from './components/dashboard/DashboardLayout';
import GuestGuard from './components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';
import ErrorBoundary from './components/ErrorBoundary';
import Novedades from './components/Novedades/Novedades';
import lazyWithRetry from './utils/lazyWithRetry'; // function to load a component with retry to avoid chunk load errors
import useAuth from './hooks/useAuth';
import { db } from './lib/firebase';

const Loadable = (Component) =>
  function load(props) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <Component {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };

// Authentication pages

const Login = Loadable(lazy(() => lazyWithRetry(() => import('./pages/authentication/Login'), 'Login')));
const PasswordRecovery = Loadable(
  lazy(() => lazyWithRetry(() => import('./pages/authentication/PasswordRecovery'), 'PasswordRecovery'))
);
const PasswordReset = Loadable(
  lazy(() => lazyWithRetry(() => import('./pages/authentication/PasswordReset'), 'PasswordReset'))
);

// Dashboard pages

const Perfil = Loadable(lazy(() => lazyWithRetry(() => import('./components/dashboard/Perfil'), 'Perfil')));
const Analytics = Loadable(lazy(() => lazyWithRetry(() => import('./pages/dashboard/Analytics'), 'Analytics')));

// Client pages

const ClientList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/clientes/ClientList'), 'ClientList'))
);
const ClientCreate = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/clientes/ClientCreate'), 'ClientCreate'))
);
const ClientEdit = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/clientes/ClientEdit'), 'ClientEdit'))
);
const ClientActivity = Loadable(
  lazy(() =>
    lazyWithRetry(() => import('./components/gestion/clientes/ClientActivity/ClientActivity'), 'ClientActivity')
  )
);

// Client Groups pages
const ClientGroupsList = Loadable(
  lazy(() =>
    lazyWithRetry(() => import('./components/gestion/clientes/ClientGroups/ClientGroupsList'), 'ClientGroupsList')
  )
);
const ClientGroupEdit = Loadable(
  lazy(() =>
    lazyWithRetry(() => import('./components/gestion/clientes/ClientGroups/ClientGroupEdit'), 'ClientGroupEdit')
  )
);
const ContractMonitor = Loadable(
  lazy(() =>
    lazyWithRetry(
      () => import('./components/gestion/clientes/SignedDocuments/ContractMonitor'),
      'ContractMonitor'
    )
  )
);

// Deudor pages

const DeudoresList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/deudores/DeudoresList'), 'DeudoresList'))
);
const DeudoresCreate = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/deudores/DeudorCreate'), 'DeudoresCreate'))
);
const DeudoresEdit = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/deudores/DeudorEdit'), 'DeudoresEdit'))
);

// Riesgo pages

const VaciadosUploader = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/riesgo/VaciadosUploader'), 'VaciadosUploader'))
);

// Garantias pages

const GarantiasList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/garantias/GarantiasList'), 'GarantiasList'))
);
const GarantiaCreate = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/garantias/GarantiaCreate'), 'GarantiaCreate'))
);
const GarantiaEdit = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/garantias/GarantiaEdit'), 'GarantiaEdit'))
);

// Seguros pages (WS-8 leasing)

const SegurosList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/seguros/SegurosList'), 'SegurosList'))
);
const SeguroDetail = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/seguros/SeguroDetail'), 'SeguroDetail'))
);
const ActivosLeasingList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/activosLeasing/ActivosLeasingList'), 'ActivosLeasingList'))
);

// Prospectos pages

const ProspectosList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/prospectos/ProspectosList'), 'ProspectosList'))
);
const ProspectoEditView = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/prospectos/ProspectoEditView'), 'ProspectoEditView'))
);

// Operations pages

const OperationsList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/operaciones/OperationsList'), 'OperationsList'))
);
const OperationCreate = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/operaciones/OperationCreate'), 'OperationCreate'))
);
const OperationEdit = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/operaciones/OperationEdit'), 'OperationEdit'))
);
const MassOperations = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/operaciones/MassOperations'), 'MassOperations'))
);

// Gastos pages

const GastosList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/gastos/GastosList'), 'GastosList'))
);

// No Cedidos pages

const NoCedidosList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/nocedidos/NoCedidosList'), 'NoCedidosList'))
);

// Recaud Extra pages

const RecaudExtraList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/recaudextra/RecaudExtraList'), 'RecaudExtraList'))
);

// Excedentes pages

const ExcedentesList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/excedentes/ExcedentesList'), 'ExcedentesList'))
);

// Giros pages

const GirosList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/giros/GirosList'), 'GirosList'))
);
const GiroView = Loadable(lazy(() => lazyWithRetry(() => import('./components/gestion/giros/GiroView'), 'GiroView')));

// Recaudacion pages

const RecaudacionList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/recaudacion/RecaudacionList'), 'RecaudacionList'))
);

// Cobranza pages

const CobranzaList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/cobranza/CobranzaList'), 'CobranzaList'))
);
const FailedEmailsList = Loadable(
  lazy(() =>
    lazyWithRetry(() => import('./components/gestion/cobranza/FailedEmailsList'), 'FailedEmailsList')
  )
);
const CobranzaView = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/cobranza/CobranzaView'), 'CobranzaView'))
);

// Reportes pages

const ReportesList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/ReportesList'), 'ReportesList'))
);
const Docsejecutivo = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsejecutivo'), 'Docsejecutivo'))
);
const Docscobranza = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docscobranza'), 'Docscobranza'))
);
const Docespecifico = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docespecifico'), 'Docespecifico'))
);
const Docspagados = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docspagados'), 'Docspagados'))
);
const Docspagadosact = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docspagadosact'), 'Docspagadosact'))
);
const Docsprorrogados = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsprorrogados'), 'Docsprorrogados'))
);
const Docsprorrogadosact = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsprorrogadosact'), 'Docsprorrogadosact'))
);
const Docsprotestadoscli = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsprotestadoscli'), 'Docsprotestadoscli'))
);
const Docsprotestadosdeu = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsprotestadosdeu'), 'Docsprotestadosdeu'))
);
const AdvancedDocumentSearch = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/AdvancedSearch'), 'DocAdvancedSearch'))
);
const Deupublicados = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Deupublicados'), 'Deupublicados'))
);
const Historicocltdeudor = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Historicocltdeudor'), 'Historicocltdeudor'))
);
const HistoricocltdeudorOld = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/HistoricocltdeudorOld'), 'HistoricocltdeudorOld'))
);
const Docspublicados = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docspublicados'), 'Docspublicados'))
);
const Docsmora = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsmora'), 'Docsmora'))
);
const Docsproblemas = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsproblemas'), 'Docsproblemas'))
);
const Docscartera = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docscartera'), 'Docscartera'))
);
const Docscarteraafecha = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docscarteraafecha'), 'Docscarteraafecha'))
);
const Docsdevengados = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsdevengados'), 'Docsdevengados'))
);
const Doccontable = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Doccontable'), 'Doccontable'))
);
const Excedentesafecha = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Excedentesafecha'), 'Excedentesafecha'))
);
const Excedentes = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Excedentes'), 'Excedentes'))
);
const Nocedidosafecha = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Nocedidosafecha'), 'Nocedidosafecha'))
);
const Nocedidos = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Nocedidos'), 'Nocedidos'))
);
const Gastosxrecuperar = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Gastosxrecuperar'), 'Gastosxrecuperar'))
);
const RecaudExtraaFecha = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Recaudextraafecha'), 'RecaudExtraaFecha'))
);
const RecaudExtra = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Recaudextra'), 'RecaudExtra'))
);
const Lineasclientes = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Lineasclientes'), 'Lineasclientes'))
);
const Adjuntosclientes = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Adjuntosclientes'), 'Adjuntosclientes'))
);
const Estadisticocartera = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Estadisticocartera'), 'Estadisticocartera'))
);
const Provisionescartera = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Provisionescartera'), 'Provisionescartera'))
);
const Informecolocaciones = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Informecolocaciones'), 'Informecolocaciones'))
);
const Gestcobranza = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Gestcobranza'), 'Gestcobranza'))
);
const Comitecobranza = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Comitecobranza'), 'Comitecobranza'))
);
const InformeMetasEjecutivos = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Informemetaseje'), 'InformeMetasEjecutivos'))
);
const InformeBonosEjecutivos = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Informebonoseje'), 'InformeBonosEjecutivos'))
);
const Informeriesgoclientes = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Informeriesgocli'), 'InformeRiesgoClientes'))
);
const ConcentracionExposicion = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/ConcentracionExposicion'), 'ConcentracionExposicion'))
);
const UtilizacionPorEjecutivo = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/UtilizacionPorEjecutivo'), 'UtilizacionPorEjecutivo'))
);
const FlujoCaja = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/FlujoCaja'), 'FlujoCaja'))
);
const Deudaporcliente = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Deudaporcliente'), 'Deudaporcliente'))
);
const Docsrecaudados = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsrecaudados'), 'Docsrecaudados'))
);
const Docsrecaudadosold = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/DocsrecaudadosOld'), 'Docsrecaudadosold'))
);
const Docsrecaudadosperiod = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsrecaudadosperiod'), 'Docsrecaudadosperiod'))
);
const Docsdevengadosback = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsdevengadosback'), 'Docsdevengadosback'))
);
const Informeingresos = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Informeingresos'), 'Informeingresos'))
);
const Facturasnuevassii = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Facturasnuevassii'), 'Facturasnuevassii'))
);
const Credencialesclientes = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Credencialesclientes'), 'Credencialesclientes'))
);
const Docsmorapagada = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsmorapagada'), 'Docsmorapagada'))
);
const Clientsvisits = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Clientsvisits'), 'Clientsvisits'))
);
const Docsreqope = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docsreqope'), 'Docsreqope'))
);
const Deudoresexport = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Deudoresexport'), 'Deudoresexport'))
);
const Docscastigadosact = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docscastigadosact'), 'Docscastigadosact'))
);
const Docscursadosrecaudope = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Docscursadosrecaudope'), 'Docscursadosrecaudope'))
);
const Opereversadas = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Opereversadas'), 'Opereversadas'))
);
const Recentlypaidclients = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Recentlypaidclients'), 'Recentlypaidclients'))
);
const Recentlyactiveclients = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Recentlyactiveclients'), 'Recentlyactiveclients'))
);
const CalidadDatosClientes = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/CalidadDatosClientes'), 'CalidadDatosClientes'))
);
const ClientsxSocio = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/ClientsxSocio'), 'ClientsxSocio'))
);
const Clientsalessegment = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Clientsalessegment'), 'Clientsalessegment'))
);
const DocsNormalizacion = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/DocsNormalizacion'), 'DocsNormalizacion'))
);

// Actividades pages

const ActividadesList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/actividades/ActividadesList'), 'ActividadesList'))
);
const ActividadesListUsers = Loadable(
  lazy(() =>
    lazyWithRetry(() => import('./components/gestion/actividades/ActividadesListUsers'), 'ActividadesListUsers')
  )
);
const ActividadView = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/actividades/ActividadView'), 'ActividadView'))
);

// Contabilidad pages

const ContabilidadList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/contabilidad/ContabilidadList'), 'ContabilidadList'))
);
const AsientoView = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/contabilidad/AsientoView'), 'AsientoView'))
);
const FacturacionList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/contabilidad/FacturacionList'), 'FacturacionList'))
);

// Configuration pages

const Users = Loadable(lazy(() => lazyWithRetry(() => import('./components/configuration/users/UserList'), 'Users')));
const UserCreate = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/users/UserCreate'), 'UserCreate'))
);
const UserEdit = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/users/UserEdit'), 'UserEdit'))
);
const ExtUserReq = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/users/ExtUserReq'), 'ExtUserReq'))
);
const MapaPermisos = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/users/MapaPermisos'), 'MapaPermisos'))
);

// Flujos pages

const FlujosList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/flujos/FlujosList'), 'FlujosList'))
);
const FlujoCreate = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/flujos/FlujoCreate'), 'FlujoCreate'))
);
const FlujoEdit = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/flujos/FlujoEdit'), 'FlujoEdit'))
);
const FlujoCreateEdit = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/flujos/FlujoCreateEdit'), 'FlujoCreateEdit'))
);

// Mantenedores pages

const ParametrosList = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/mantenedores/ParametrosList'), 'ParametrosList'))
);

const Feriados = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/mantenedores/Feriados'), 'Feriados'))
);
const DocumentTemplates = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/configuration/mantenedores/DocumentTemplates'), 'DocumentTemplates'))
);
const SegregacionFunciones = Loadable(
  lazy(() =>
    lazyWithRetry(() => import('./components/configuration/mantenedores/SegregacionFunciones'), 'SegregacionFunciones')
  )
);

// Error pages

const AuthorizationRequired = Loadable(
  lazy(() => lazyWithRetry(() => import('./pages/AuthorizationRequired'), 'AuthorizationRequired'))
);
const NotFound = Loadable(lazy(() => lazyWithRetry(() => import('./pages/NotFound'), 'NotFound')));
const ServerError = Loadable(lazy(() => lazyWithRetry(() => import('./pages/ServerError'), 'ServerError')));

// Relaciones Societarias pages

const RelacionesSocietarias = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/relaciones'), 'RelacionesSocietarias'))
);

// Other pages

const Home = Loadable(lazy(() => lazyWithRetry(() => import('./pages/Home'), 'Home')));
const BugReportForm = Loadable(lazy(() => lazyWithRetry(() => import('./components/reporting/BugReportForm'))));
const Roadmap = Loadable(lazy(() => lazyWithRetry(() => import('./components/gestion/roadmap'), 'Roadmap')));
const Recursos = Loadable(lazy(() => lazyWithRetry(() => import('./components/userGuides'), 'Recursos')));
const Usersxclient = Loadable(
  lazy(() => lazyWithRetry(() => import('./components/gestion/reportes/Usersxclient'), 'Usersxclient'))
);

function CheckRoleReport({ reportPath, children }) {
  const { user } = useAuth();
  const [reportsList, setReportsList] = useState(null);

  useEffect(() => {
    let cancelled = false;
    db.collection('params')
      .doc('reportsList')
      .get()
      .then((snapshot) => {
        if (cancelled) return;
        const data = snapshot.data();
        setReportsList(data?.reportsList ?? []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching reports list:', error);
        setReportsList([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (reportsList === null) {
    return null;
  }

  const report = reportsList.find((r) => r.path === reportPath);
  if (user?.perfil?.reportes?.includes(report?.value)) {
    return children;
  }
  return <Navigate to="/401" />;
}

// Element-level guard by role permiso code (e.g. 'f79f', or 'f1f|f124f' for either). Defense-in-depth
// for routes whose access is otherwise only hidden at the sidebar level.
function CheckRolePermiso({ permiso, children }) {
  const { user } = useAuth();
  if (user?.role && new RegExp(permiso).test(user.role)) {
    return children;
  }
  return <Navigate to="/401" />;
}

const routes = [
  {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      { path: 'login-unguarded', element: <Login /> },
      { path: 'password-recovery', element: <PasswordRecovery /> },
      { path: 'password-reset', element: <PasswordReset /> },
    ],
  },
  {
    path: 'panel',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { path: '', element: <Analytics /> },
      { path: 'perfil', element: <Perfil /> },
    ],
  },
  {
    path: 'gestion',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: 'clientes',
        children: [
          { path: 'actividad', element: <ClientActivity /> },
          { path: '', element: <ClientList /> },
          { path: 'crear-cliente', element: <ClientCreate /> },
          { path: ':clientId', element: <ClientEdit /> },
          {
            path: 'grupos',
            children: [
              { path: '', element: <ClientGroupsList /> },
              { path: 'crear', element: <ClientGroupEdit /> },
              { path: ':groupId', element: <ClientGroupEdit /> },
            ],
          },
        ],
      },
      { path: 'firmas-docs', element: <ContractMonitor /> },
      {
        path: 'garantias',
        children: [
          { path: '', element: <GarantiasList /> },
          { path: 'crear-garantia', element: <GarantiaCreate /> },
          { path: ':garantiaId', element: <GarantiaEdit /> },
        ],
      },
      {
        path: 'seguros',
        children: [
          {
            path: '',
            element: (
              <CheckRolePermiso permiso="f121f">
                <SegurosList />
              </CheckRolePermiso>
            ),
          },
          {
            path: ':seguroId',
            element: (
              // f123f (Ver Activos en Leasing) abre el detalle en solo lectura: las acciones exigen f121f.
              // CheckRolePermiso arma un RegExp, así que el | acepta cualquiera de los dos.
              <CheckRolePermiso permiso="f121f|f123f">
                <SeguroDetail />
              </CheckRolePermiso>
            ),
          },
        ],
      },
      {
        path: 'activos-leasing',
        element: (
          <CheckRolePermiso permiso="f123f">
            <ActivosLeasingList />
          </CheckRolePermiso>
        ),
      },
      { path: 'vaciados', children: [{ path: '', element: <VaciadosUploader /> }] },
      {
        path: 'deudores',
        children: [
          { path: '', element: <DeudoresList /> },
          { path: 'crear-deudor', element: <DeudoresCreate /> },
          { path: ':deudorId', element: <DeudoresEdit /> },
        ],
      },
      {
        path: 'prospectos',
        children: [
          { path: '', element: <ProspectosList /> },
          { path: ':clientId/:id', element: <ProspectoEditView /> },
        ],
      },
      {
        path: 'operaciones',
        children: [
          { path: '', element: <OperationsList /> },
          { path: 'crear-operacion', element: <OperationCreate /> },
          { path: 'mass-operations', element: <MassOperations /> },
          { path: ':clientId/:operationId', element: <OperationEdit /> },
        ],
      },
      {
        path: 'giros',
        children: [
          { path: '', element: <GirosList /> },
          { path: ':giroId', element: <GiroView /> },
        ],
      },
      {
        path: 'recaudacion',
        children: [
          { path: '', element: <RecaudacionList /> },
          { path: 'busqueda', element: <AdvancedDocumentSearch /> },
        ],
      },
      { path: 'gastos', children: [{ path: '', element: <GastosList /> }] },
      { path: 'nocedidos', children: [{ path: '', element: <NoCedidosList /> }] },
      { path: 'recaudextra', children: [{ path: '', element: <RecaudExtraList /> }] },
      { path: 'excedentes', children: [{ path: '', element: <ExcedentesList /> }] },
      {
        path: 'actividades',
        children: [
          { path: '', element: <ActividadesList /> },
          { path: ':actividadId', element: <ActividadView /> },
        ],
      },
      {
        path: 'actividades-clientes',
        children: [
          { path: '', element: <ActividadesListUsers /> },
          { path: ':actividadId', element: <ActividadView /> },
        ],
      },
      {
        path: 'cobranza',
        children: [
          { path: '', element: <CobranzaList /> },
          {
            path: 'correos-fallidos',
            element: (
              <CheckRolePermiso permiso="f79f">
                <FailedEmailsList />
              </CheckRolePermiso>
            ),
          },
          { path: ':clientId/:operationId/:documentId/:cobranzaId', element: <CobranzaView /> },
        ],
      },
      {
        path: 'contabilidad',
        children: [
          { path: '', element: <ContabilidadList /> },
          { path: ':asientoId', element: <AsientoView /> },
        ],
      },
      { path: 'facturacion', children: [{ path: '', element: <FacturacionList /> }] },
      {
        path: 'reportes',
        children: [
          { path: '', element: <ReportesList /> },
          {
            path: 'codigo/deupublicados',
            element: (
              <CheckRoleReport reportPath="deupublicados">
                <Deupublicados />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsejecutivo',
            element: (
              <CheckRoleReport reportPath="docsejecutivo">
                <Docsejecutivo />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docscobranza',
            element: (
              <CheckRoleReport reportPath="docscobranza">
                <Docscobranza />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docespecifico',
            element: (
              <CheckRoleReport reportPath="docespecifico">
                <Docespecifico />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docspagados',
            element: (
              <CheckRoleReport reportPath="docspagados">
                <Docspagados />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docspagadosact',
            element: (
              <CheckRoleReport reportPath="docspagadosact">
                <Docspagadosact />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsprorrogados',
            element: (
              <CheckRoleReport reportPath="docsprorrogados">
                <Docsprorrogados />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsprorrogadosact',
            element: (
              <CheckRoleReport reportPath="docsprorrogadosact">
                <Docsprorrogadosact />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsprotestadoscli',
            element: (
              <CheckRoleReport reportPath="docsprotestadoscli">
                <Docsprotestadoscli />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsprotestadosdeu',
            element: (
              <CheckRoleReport reportPath="docsprotestadosdeu">
                <Docsprotestadosdeu />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/historicocltdeudor',
            element: (
              <CheckRoleReport reportPath="historicocltdeudor">
                <Historicocltdeudor />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/historicocltdeudorold',
            element: (
              <CheckRoleReport reportPath="historicocltdeudorold">
                <HistoricocltdeudorOld />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docspublicados',
            element: (
              <CheckRoleReport reportPath="docspublicados">
                <Docspublicados />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsmora',
            element: (
              <CheckRoleReport reportPath="docsmora">
                <Docsmora />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsproblemas',
            element: (
              <CheckRoleReport reportPath="docsproblemas">
                <Docsproblemas />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docscartera',
            element: (
              <CheckRoleReport reportPath="docscartera">
                <Docscartera />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docscarteraafecha',
            element: (
              <CheckRoleReport reportPath="docscarteraafecha">
                <Docscarteraafecha />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/doccontable',
            element: (
              <CheckRoleReport reportPath="doccontable">
                <Doccontable />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/excedentesafecha',
            element: (
              <CheckRoleReport reportPath="excedentesafecha">
                <Excedentesafecha />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/excedentes',
            element: (
              <CheckRoleReport reportPath="excedentes">
                <Excedentes />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/nocedidosafecha',
            element: (
              <CheckRoleReport reportPath="nocedidosafecha">
                <Nocedidosafecha />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/nocedidos',
            element: (
              <CheckRoleReport reportPath="nocedidos">
                <Nocedidos />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/gastos',
            element: (
              <CheckRoleReport reportPath="gastos">
                <Gastosxrecuperar />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/recaudextra',
            element: (
              <CheckRoleReport reportPath="recaudextra">
                <RecaudExtra />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/recaudextraafecha',
            element: (
              <CheckRoleReport reportPath="recaudextraafecha">
                <RecaudExtraaFecha />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/lineasclientes',
            element: (
              <CheckRoleReport reportPath="lineasclientes">
                <Lineasclientes />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/adjuntosclientes',
            element: (
              <CheckRoleReport reportPath="adjuntosclientes">
                <Adjuntosclientes />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/clientsvisits',
            element: (
              <CheckRoleReport reportPath="clientsvisits">
                <Clientsvisits />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/provisionescartera',
            element: (
              <CheckRoleReport reportPath="provisionescartera">
                <Provisionescartera />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/estadisticocartera',
            element: (
              <CheckRoleReport reportPath="estadisticocartera">
                <Estadisticocartera />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsdevengados',
            element: (
              <CheckRoleReport reportPath="docsdevengados">
                <Docsdevengados />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/informecolocaciones',
            element: (
              <CheckRoleReport reportPath="informecolocaciones">
                <Informecolocaciones />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/gestcobranza',
            element: (
              <CheckRoleReport reportPath="gestcobranza">
                <Gestcobranza />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/comitecobranza',
            element: (
              <CheckRoleReport reportPath="comitecobranza">
                <Comitecobranza />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/informemetaseje',
            element: (
              <CheckRoleReport reportPath="informemetaseje">
                <InformeMetasEjecutivos />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/informebonoseje',
            element: (
              <CheckRoleReport reportPath="informebonoseje">
                <InformeBonosEjecutivos />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/informeriesgocli',
            element: (
              <CheckRoleReport reportPath="informeriesgocli">
                <Informeriesgoclientes />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/concentracionexposicion',
            element: (
              <CheckRoleReport reportPath="concentracionexposicion">
                <ConcentracionExposicion />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/utilizacionporejecutivo',
            element: (
              <CheckRoleReport reportPath="utilizacionporejecutivo">
                <UtilizacionPorEjecutivo />
              </CheckRoleReport>
            ),
          },
          {
            // Ruta anterior del reporte (renombrado 2026-08-27): marcadores y enlaces viejos.
            path: 'codigo/forecastpagos',
            element: <Navigate to="/gestion/reportes/codigo/flujocaja" replace />,
          },
          {
            path: 'codigo/flujocaja',
            element: (
              <CheckRoleReport reportPath="flujocaja">
                <FlujoCaja />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/deudaporcliente',
            element: (
              <CheckRoleReport reportPath="deudaporcliente">
                <Deudaporcliente />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsrecaudados',
            element: (
              <CheckRoleReport reportPath="docsrecaudados">
                <Docsrecaudados />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsrecaudadosold',
            element: (
              <CheckRoleReport reportPath="docsrecaudadosold">
                <Docsrecaudadosold />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsrecaudadosperiod',
            element: (
              <CheckRoleReport reportPath="docsrecaudadosperiod">
                <Docsrecaudadosperiod />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/credencialesclientes',
            element: (
              <CheckRoleReport reportPath="credencialesclientes">
                <Credencialesclientes />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsmorapagada',
            element: (
              <CheckRoleReport reportPath="docsmorapagada">
                <Docsmorapagada />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsreqope',
            element: (
              <CheckRoleReport reportPath="docsreqope">
                <Docsreqope />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/deudoresexport',
            element: (
              <CheckRoleReport reportPath="deudoresexport">
                <Deudoresexport />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docscastigadosact',
            element: (
              <CheckRoleReport reportPath="docscastigadosact">
                <Docscastigadosact />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docscursadosrecaudope',
            element: (
              <CheckRoleReport reportPath="docscursadosrecaudope">
                <Docscursadosrecaudope />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/opereversadas',
            element: (
              <CheckRoleReport reportPath="opereversadas">
                <Opereversadas />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/docsdevengadosback',
            element: (
              <CheckRoleReport reportPath="docsdevengadosback">
                <Docsdevengadosback />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/informeingresos',
            element: (
              <CheckRoleReport reportPath="informeingresos">
                <Informeingresos />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/facturasnuevassii',
            element: (
              <CheckRoleReport reportPath="facturasnuevassii">
                <Facturasnuevassii />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/recentlypaidclients',
            element: (
              <CheckRoleReport reportPath="recentlypaidclients">
                <Recentlypaidclients />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/recentlyactiveclients',
            element: (
              <CheckRoleReport reportPath="recentlyactiveclients">
                <Recentlyactiveclients />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/clientsalessegment',
            element:(
              <CheckRoleReport reportPath="clientsalessegment">
                <Clientsalessegment />
              </CheckRoleReport>
            )
          },
          {
            path: 'codigo/docsnormalizacion',
            element: (
              <CheckRoleReport reportPath="docsnormalizacion">
                <DocsNormalizacion />
              </CheckRoleReport>
            )
          },
          {
            path: 'codigo/usersxclient',
            element: (
              <CheckRoleReport reportPath="usersxclient">
                <Usersxclient />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/calidaddatosclientes',
            element: (
              <CheckRoleReport reportPath="calidaddatosclientes">
                <CalidadDatosClientes />
              </CheckRoleReport>
            ),
          },
          {
            path: 'codigo/clientsxsocio',
            element: (
              <CheckRoleReport reportPath="clientsxsocio">
                <ClientsxSocio />
              </CheckRoleReport>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'visualizar/relaciones',
    element: (
      <AuthGuard>
        <RelacionesSocietarias />
      </AuthGuard>
    ),
  },
  {
    path: 'configuracion',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: 'usuarios',
        children: [
          { path: '', element: <Users /> },
          { path: 'crear-usuario', element: <UserCreate /> },
          {
            path: 'mapa-permisos',
            // f124f marks separation-of-duties conflicts as seen there, without administering users.
            element: (
              <CheckRolePermiso permiso="f1f|f124f">
                <MapaPermisos />
              </CheckRolePermiso>
            ),
          },
          { path: ':userId', element: <UserEdit /> },
        ],
      },
      { path: 'extuserreq', children: [{ path: ':reqId', element: <ExtUserReq /> }] },
      {
        path: 'mantenedores',
        children: [
          { path: '', element: <ParametrosList /> },
          { path: 'document-templates', element: <DocumentTemplates /> },
          { path: 'feriados', element: <Feriados /> },
          {
            path: 'segregacion-funciones',
            element: (
              <CheckRolePermiso permiso="f124f">
                <SegregacionFunciones />
              </CheckRolePermiso>
            ),
          },
        ],
      },
      {
        path: 'flujos',
        children: [
          { path: '', element: <FlujosList /> },
          { path: 'crear-flujo', element: <FlujoCreate /> },
          { path: 'crear-editar/:flujoId', element: <FlujoCreateEdit /> },
          { path: ':flujoId', element: <FlujoEdit /> },
        ],
      },
      { path: 'feedback', children: [{ path: '', element: <BugReportForm /> }] },
      { path: 'centrorecursos', children: [{ path: '', element: <Recursos /> }] },
      { path: 'roadmap', children: [{ path: '', element: <Roadmap /> }] },
      { path: 'novedades', children: [{ path: '', element: <Novedades /> }] },
    ],
  },
  {
    path: '*',
    element: <MainLayout />,
    children: [
      { path: '', element: <Home /> },
      { path: '401', element: <AuthorizationRequired /> },
      { path: '404', element: <NotFound /> },
      { path: '500', element: <ServerError /> },
      { path: '*', element: <NotFound /> },
    ],
  },
];

export default routes;
